/**
 * Lectura reactiva de los datos locales y cálculo del día lógico actual.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { logicalDateFor, type LocalDate } from '@habitos/engine';
import { SETTINGS_ID } from '@habitos/protocol';
import { db } from '../db/db';
import {
  decode,
  type EntryRec,
  type GroupRec,
  type HabitRec,
  type OverrideRec,
  type FocusRec,
  type JournalRec,
  type ListItemRec,
  type MoveRec,
  type NoteRec,
  type ReminderRec,
  type SettingsRec,
  type VacationRec,
} from './model';
import type { Ctx } from './actions';

export type { AppData } from '@habitos/assistant/domain/data';
import type { AppData } from '@habitos/assistant/domain/data';

/** Lectura de todos los datos locales vivos (también fuera de React, p. ej. entre pasos de un plan). */
export async function loadAppData(): Promise<AppData> {
  const [settings, habits, entries, overrides, groups, vacations, reminders, notes, listItems, journal, focus, moves] = await Promise.all([
    db.settings.get(SETTINGS_ID),
    db.habits.toArray(),
    db.entries.toArray(),
    db.overrides.toArray(),
    db.groups.toArray(),
    db.vacations.toArray(),
    db.reminders.toArray(),
    db.notes.toArray(),
    db.listItems.toArray(),
    db.journal.toArray(),
    db.focus.toArray(),
    db.moves.toArray(),
  ]);
  return {
    settings: settings ? decode<SettingsRec>(settings) : null,
    habits: habits.map((r) => decode<HabitRec>(r)),
    entries: entries.map((r) => decode<EntryRec>(r)).filter((e) => !e.deletedAt),
    overrides: overrides.map((r) => decode<OverrideRec>(r)).filter((o) => !o.deletedAt),
    groups: groups.map((r) => decode<GroupRec>(r)),
    vacations: vacations.map((r) => decode<VacationRec>(r)).filter((v) => !v.deletedAt),
    reminders: reminders.map((r) => decode<ReminderRec>(r)).filter((r) => !r.deletedAt),
    notes: notes.map((r) => decode<NoteRec>(r)).filter((n) => !n.deletedAt),
    listItems: listItems.map((r) => decode<ListItemRec>(r)).filter((i) => !i.deletedAt),
    journal: journal.map((r) => decode<JournalRec>(r)).filter((j) => !j.deletedAt),
    focus: focus.map((r) => decode<FocusRec>(r)).filter((f) => !f.deletedAt),
    moves: moves.map((r) => decode<MoveRec>(r)).filter((m) => !m.deletedAt),
  } satisfies AppData;
}

export function useAppData(): AppData | undefined {
  return useLiveQuery(loadAppData, []);
}

/** Instante actual que se refresca cada 30 s y al volver a la pestaña. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [intervalMs]);
  return now;
}

export function useCtx(settings: SettingsRec | null): Ctx | null {
  const now = useNow();
  const today: LocalDate | null = settings ? logicalDateFor(now, settings.effectiveTz, settings.dayStartMinutes) : null;
  return useMemo(() => {
    if (!settings || !today) return null;
    return { today, tz: settings.effectiveTz, settings };
  }, [settings, today]);
}
